import { LDFlagSet } from 'launchdarkly-react-client-sdk';
// Files in the same folder
import { AuthAction } from './actions';
// Types
import { AffiliateDetailsAttributes, KeycloakUserDetails, RoleAccessFunction } from '../../../core/types/AffiliateDetails';
import {
  USER_AUTH_REQUEST_START,
  USER_AUTH_REQUEST_FAIL,
  USER_AUTH_REQUEST_SUCCESS,
  SET_USER_AUTH_STATE,
  USER_AFFILIATE_DETAILS_REQUEST_START,
  USER_AFFILIATE_DETAILS_REQUEST_FAIL,
  USER_AFFILIATE_DETAILS_REQUEST_SUCCESS,
  UPDATE_USER_AFFILIATE_DETAILS_REQUEST_START,
  UPDATE_USER_AFFILIATE_DETAILS_REQUEST_FAIL,
  UPDATE_USER_AFFILIATE_DETAILS_REQUEST_SUCCESS,
  UPDATE_SETTINGS_STATUS_TO_INITIAL,
  UPDATE_LAUNCH_DARKLY_FLAG_CONFIG,
  SET_KEYCLOAK_USER_DETAILS,
  SET_USER_AUTH_DETAIL_STATE,
  SET_USER_INITIAL_AUTH_DETAIL_STATE,
  SET_LOGIN_API_ERROR,
  SET_SUPER_USER_STATE,
} from './types';
// Utils
import { getRoleAccessFunction } from '../../../core/utils/GetRoleAccessFunction';

type AuthState = {
  isAuthenticated: boolean | null;
  accessToken: string | null;
  refreshToken: string | null;
  loadingState: string;
  affiliateDetails: AffiliateDetailsAttributes | null;
  updateSettingsStatus: string;
  affiliateLoadingState: string;
  updateDetailsErrorCode: string;
  launchDarklyFlags: object | LDFlagSet;
  roleAccessFunction: RoleAccessFunction;
  keycloakUserDetails: KeycloakUserDetails;
  isLoginApiError: object;
  isSuperUser: boolean;
};

const INITIAL_STATE: AuthState = {
  isAuthenticated: null,
  accessToken: null,
  refreshToken: null,
  loadingState: 'UNINITIALISED',
  affiliateDetails: null,
  updateSettingsStatus: 'UNINITIALISED',
  affiliateLoadingState: 'UNINITIALISED',
  updateDetailsErrorCode: '',
  launchDarklyFlags: {},
  roleAccessFunction: {
    VIEW_ACCOUNT: false,
    VIEW_BANK_DETAILS: false,
    VIEW_CLIENT_MANAGEMENT: false,
    REGISTER_CLIENT: false,
    VIEW_INVOICES: false,
    VIEW_COMMISSION: false,
    REFER_CLIENT: false,
    VIEW_REFERRALS: false,
    VIEW_NOTIFICATIONS: false,
    VIEW_RESOURCES: false,
    VIEW_SETTINGS: false,
    BANK_DOCUMENT_UPLOAD: false,
  },
  keycloakUserDetails: {
    access_token: null,
    refresh_token: null,
    expires_in: null,
    affiliate_number: null,
    affiliate_id: null,
    username: null,
    isKeycloakAuthenticated: false
  },
  isLoginApiError: {
    shouldTriggerError: false,
    errorType: ''
  },
  isSuperUser: false
};

export const reducer = (
  state = INITIAL_STATE,
  action: AuthAction
): AuthState => {
  switch (action.type) {
    case USER_AUTH_REQUEST_START:
      return {
        ...state,
        loadingState: 'LOADING'
      };

    case USER_AUTH_REQUEST_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        loadingState: 'LOADED'
      };

    case USER_AUTH_REQUEST_FAIL:
      return {
        ...state,
        loadingState: 'ERROR'
      };

    case SET_USER_AUTH_STATE:
      return {
        ...state,
        isAuthenticated: action.payload
      };

    case SET_USER_AUTH_DETAIL_STATE:
      const { access_token, refresh_token } = action.payload;

      return {
        ...state,
        accessToken: access_token,
        refreshToken: refresh_token,
      };

    case SET_USER_INITIAL_AUTH_DETAIL_STATE:
      return {
        ...state,
        isAuthenticated: null,
        accessToken: null,
        refreshToken: null,
        loadingState: 'UNINITIALISED',
        affiliateDetails: null,
        updateSettingsStatus: 'UNINITIALISED',
        affiliateLoadingState: 'UNINITIALISED',
        isLoginApiError: {
          shouldTriggerError: false,
          errorType: ''
        },
        isSuperUser: false
      };

    case USER_AFFILIATE_DETAILS_REQUEST_START:
      return {
        ...state,
        affiliateLoadingState: 'LOADING'
      };

    case USER_AFFILIATE_DETAILS_REQUEST_FAIL:
      return {
        ...state,
        affiliateLoadingState: 'ERROR'
      };

    case USER_AFFILIATE_DETAILS_REQUEST_SUCCESS:
      return {
        ...state,
        affiliateDetails: action.payload,
        affiliateLoadingState: 'LOADED',
        roleAccessFunction: {
          ...state.roleAccessFunction,
          ...getRoleAccessFunction(action.payload, state.launchDarklyFlags, state.isSuperUser)
        }
      };

    case UPDATE_USER_AFFILIATE_DETAILS_REQUEST_START:
      return {
        ...state,
        updateSettingsStatus: 'UNINITIALISED',
        loadingState: 'UPDATING'
      };

    case UPDATE_USER_AFFILIATE_DETAILS_REQUEST_FAIL:
      const { response: errorResponse } = action.payload;
      return {
        ...state,
        updateSettingsStatus: 'error',
        loadingState: 'ERROR',
        updateDetailsErrorCode: errorResponse?.data?.code
      };

    case UPDATE_USER_AFFILIATE_DETAILS_REQUEST_SUCCESS:
      return {
        ...state,
        updateSettingsStatus: 'success',
        loadingState: 'UPDATED'
      };

    case UPDATE_SETTINGS_STATUS_TO_INITIAL:
      return {
        ...state,
        updateSettingsStatus: 'UNINITIALISED',
        updateDetailsErrorCode: ''
      };
    case UPDATE_LAUNCH_DARKLY_FLAG_CONFIG:
      return {
        ...state,
        launchDarklyFlags: action.payload
      };
    case SET_KEYCLOAK_USER_DETAILS:
      return {
        ...state,
        keycloakUserDetails: action.payload
      };
    case SET_LOGIN_API_ERROR:
      return {
        ...state,
        isLoginApiError: action.payload
      };
    case SET_SUPER_USER_STATE:
      return {
        ...state,
        isSuperUser: action.payload,
        roleAccessFunction: {
          ...state.roleAccessFunction,
          ...getRoleAccessFunction(state.affiliateDetails, state.launchDarklyFlags, action.payload)
        }
      };
    default:
      return state;
  }
};
